import React from 'react';
import { useTranslation } from 'react-i18next';
import { LogOut } from 'lucide-react';
import { cn } from '../../utils/cn';

/**
 * Resolves a display name for the signed-in account.
 *
 * Precedence: user_metadata.full_name > user_metadata.name >
 * user_metadata.display_name > email > null.
 *
 * This is DATA, never UI copy — never pass the result through t() (CLAUDE.md
 * i18n rule H2). user_metadata is user-writable in Supabase, so every
 * candidate is treated as untrusted-shaped: optional-chained and checked for
 * non-empty, non-whitespace-only strings before use.
 */
export const getUserDisplayName = (user) => {
  const candidates = [
    user?.user_metadata?.full_name,
    user?.user_metadata?.name,
    user?.user_metadata?.display_name,
    user?.email,
  ];

  for (const candidate of candidates) {
    if (typeof candidate === 'string' && candidate.trim() !== '') {
      return candidate;
    }
  }

  return null;
};

/**
 * Always-visible sidebar footer: signed-in account identity + Logout.
 *
 * Pure presentational component — no context/hook dependencies other than
 * useTranslation, so it stays trivially testable and cannot accidentally
 * re-couple to workshop mode. The caller (AppSidebar) is responsible for
 * mounting this OUTSIDE any workshop-mode guard.
 */
const SidebarAccountFooter = ({ user, onLogout, isCollapsed }) => {
  const { t } = useTranslation('common');
  const displayName = getUserDisplayName(user);

  return (
    <div className="border-t border-gray-200 flex-shrink-0">
      {!isCollapsed && displayName && (
        <div className="px-3 py-2 text-sm truncate" title={displayName}>
          <span className="block text-xs text-gray-400">{t('sidebar.account.signedInAs')}</span>
          <span className="block truncate text-gray-700">{displayName}</span>
        </div>
      )}
      <div className="p-2">
        <button
          onClick={onLogout}
          className={cn(
            'w-full flex items-center justify-start h-10 px-3 rounded-md text-sm font-medium transition-colors',
            'text-gray-700 hover:bg-gray-100 hover:text-red-600 focus:outline-none focus:ring-2 focus:ring-red-500',
          )}
        >
          <LogOut className="h-5 w-5 mr-3 flex-shrink-0" />
          {!isCollapsed && <span className="truncate">{t('sidebar.logout')}</span>}
        </button>
      </div>
    </div>
  );
};

export default SidebarAccountFooter;
